import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { AuthService } from '@/app/core/auth/auth.service';
import { ToastService } from '@/app/shared/services/toast.service';
import { environment } from '@/environments/environment';

/**
 * Whether a request targets our own backend API.
 *
 * The bearer token is only meaningful to our API, so it must never leak onto
 * third-party origins (e.g. Google Apps Script). Attaching `Authorization` to a
 * cross-origin request also forces a CORS preflight the third party won't answer,
 * which silently blocks the call in the browser.
 */
const isApiRequest = (url: string): boolean => url.startsWith(environment.apiUrl);

/**
 * Extract a human-readable message from an HTTP error without assuming shape.
 */
const messageFromError = (error: HttpErrorResponse): string => {
    const body: unknown = error.error;
    if (
        body &&
        typeof body === 'object' &&
        'message' in body &&
        typeof (body as { message: unknown }).message === 'string'
    ) {
        return (body as { message: string }).message;
    }
    return error.message || 'Something went wrong';
};

/**
 * Attach the bearer token and sign the user out on 401 responses.
 */
export const authInterceptor = (
    req: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const toastService = inject(ToastService);
    const router = inject(Router);

    // Only our own backend gets the token and the 401 sign-out treatment.
    // Third-party origins (Google Apps Script, etc.) pass through untouched.
    const ownApi = isApiRequest(req.url);

    // Attach the Authorization header when a token is present.
    let newReq = req;
    if (ownApi && authService.accessToken) {
        newReq = req.clone({
            headers: req.headers.set(
                'Authorization',
                'Bearer ' + authService.accessToken
            ),
        });
    }

    return next(newReq).pipe(
        catchError((error) => {
            // Unauthorized → clear the session and bounce to sign-in.
            if (
                ownApi &&
                error instanceof HttpErrorResponse &&
                error.status === 401
            ) {
                authService.clearAllAuthData();
                toastService.showToast(messageFromError(error), 'error');
                router.navigateByUrl(
                    `/auth/sign-in?redirectURL=${encodeURIComponent(router.url)}`
                );
            }

            return throwError(() => error);
        })
    );
};
